import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ChevronRightIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
  dateToString,
  ItemStatus,
  updateUrlWithException,
  StringLimit,
} from "../../Utils";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { PMNotice } from "../../utils/notifications";
import { PMPagination } from "../../utils/PMPagination";
import { fetchWorkOrders } from "../../../api/work-order";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { getUsersAsync } from "../../../slices/user";
import { WORK_ORDER_STATUS_INCLUSION } from "./utils";
import { WorkOrderBulkCreateOrderDialog } from "./work-order-bulk-create-order-dialog";

export const WorkOrderStacklist = (props) => {
  const { onStatsChange } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const usersState = useSelector((s) => s.users);
  const currentUser = useSelector((s) => s.session.currentUser);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const navigate = useNavigate();

  const [controller, setController] = useState({
    filters: [...(props?.filters || [])],
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 25,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "updated_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
  });

  const getInitialFilters = () => {
    const query = searchParams.get("query") || "";
    const statusesParam = searchParams.get("statuses");
    const executorsParam = searchParams.get("executors");
    let statuses = [];
    let users = [];

    if (statusesParam) {
      try {
        statuses = JSON.parse(statusesParam);
      } catch (e) {
        console.warn("Invalid statuses parameter in URL:", statusesParam);
        statuses = [];
      }
    }

    if (executorsParam) {
      try {
        users = JSON.parse(executorsParam);
      } catch (e) {
        console.warn("Invalid executors parameter in URL:", executorsParam);
        users = [];
      }
    }

    return {
      query,
      statuses,
      users: Array.isArray(users) ? users : [],
      invoiced: false,
      finished: false,
    };
  };

  const [filters, setFilters] = useState(getInitialFilters);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const STACKLIST_MERGE_FIELDS = useMemo(
    () => ["is_completed", "order", "sales_income_included_in_project"],
    []
  );

  const effectiveFieldsParam = useMemo(() => {
    if (!props?.fields) return undefined;
    const set = new Set(
      String(props.fields)
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    );
    STACKLIST_MERGE_FIELDS.forEach((f) => set.add(f));
    return Array.from(set).join(",");
  }, [props?.fields, STACKLIST_MERGE_FIELDS]);

  const canUseBulkOrderTool = useMemo(() => {
    const roleNames = (currentUser?.roles || []).map((r) => r.name);
    if (roleNames.includes("admin")) return true;
    return roleNames.includes("projects_work-orders_edit");
  }, [currentUser]);

  const canSelectWorkOrderForBulk = useCallback(
    (item) => {
      if (!item?.id) return false;
      if (!item.is_completed) return false;
      if (item.sales_income_included_in_project) return false;
      if (["cancelled", "invoiced", "draft", "ongoing"].includes(item.status))
        return false;
      if (item.order?.id) return false;
      const roleNames = (currentUser?.roles || []).map((r) => r.name);
      if (roleNames.includes("admin")) return true;
      if (!roleNames.includes("projects_work-orders_edit")) return false;
      const role = item.role_for_current_user;
      return ["RESPONSIBLE", "RESPONSIBLE_EXECUTOR"].includes(role);
    },
    [currentUser]
  );

  const customCheckboxButtonFilters = [
    {
      key: "invoiced",
      label: t("Invoiced"),
      icon: DocumentTextIcon,
      defaultValue: false,
    },
    {
      key: "finished",
      label: t("Finished"),
      icon: CheckCircleIcon,
      defaultValue: false,
    },
  ];

  useEffect(() => {
    updateUrlWithException(
      {
        query: filters.query,
        statuses:
          filters.statuses.length > 0 ? JSON.stringify(filters.statuses) : null,
        executors:
          filters.users?.length > 0 ? JSON.stringify(filters.users) : null,
        page: controller.page,
        per_page: controller.per_page,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      "filters"
    );
  }, [
    filters,
    controller.page,
    controller.per_page,
    controller.sort,
    controller.sortBy,
    controller.view,
  ]);

  const getWorkOrders = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const filtersArray = [];

      if (filters?.statuses && filters.statuses.length > 0) {
        filtersArray.push({
          name: "status",
          value: JSON.stringify(filters.statuses),
        });
      }

      if (filters?.invoiced) {
        filtersArray.push({
          name: "invoiced",
          value: true,
        });
      }

      if (filters?.finished) {
        filtersArray.push({
          name: "finished",
          value: true,
        });
      }

      if (filters?.users?.length > 0) {
        filtersArray.push({
          name: "responsible_executor_id",
          value: JSON.stringify(filters.users.map((u) => String(u))),
        });
      }

      const allFilters = [...(props?.filters || []), ...filtersArray];

      const filteredController = {
        ...controller,
        query: filters?.query || "",
        filters: allFilters,
        ...(effectiveFieldsParam && { fields: effectiveFieldsParam }),
        ...(props?.per_page && { per_page: props.per_page }),
      };

      const response = await fetchWorkOrders(filteredController);
      if (response.status !== 200) {
        throw new Error(`Server error: ${response.status}`);
      }
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      setData(rows);
      setTotalCount(
        typeof response?.data?.totalCount === "number"
          ? response.data.totalCount
          : 0
      );
      setCurrentCount(
        typeof response?.data?.currentCount === "number"
          ? response.data.currentCount
          : rows.length
      );
    } catch (err) {
      toast.error("Something went wrong when trying to load the stats");
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [controller, filters, props?.filters, effectiveFieldsParam, props?.per_page]);

  useEffect(() => {
    getWorkOrders();
  }, [id, getWorkOrders]);

  useEffect(() => {
    onStatsChange?.({ totalCount, currentCount });
  }, [totalCount, currentCount, onStatsChange]);

  useEffect(() => {
    dispatch(
      getUsersAsync({
        filters: [{ name: "employee", value: true }],
        page: 1,
        per_page: 200,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,displayTitle,name",
      })
    );
  }, [dispatch]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setController((c) => ({ ...c, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setController((c) => ({ ...c, page: newPage }));
  };

  const executorUserOptions = (usersState?.data || []).map((u) => ({
    value: String(u.id),
    label: u.displayTitle || u.name || String(u.id),
  }));

  const normalizeWoId = useCallback((id) => Number(id), []);

  const toggleRowSelection = useCallback(
    (id, allowed) => {
      if (!allowed) return;
      const nid = normalizeWoId(id);
      if (Number.isNaN(nid)) return;
      setSelectedIds((prev) =>
        prev.some((x) => Number(x) === nid)
          ? prev.filter((x) => Number(x) !== nid)
          : [...prev, nid]
      );
    },
    [normalizeWoId]
  );

  const pageSelectableIds = useMemo(
    () =>
      (data || [])
        .filter((item) => canSelectWorkOrderForBulk(item))
        .map((item) => normalizeWoId(item.id))
        .filter((id) => !Number.isNaN(id)),
    [data, canSelectWorkOrderForBulk, normalizeWoId]
  );

  const allPageSelected =
    pageSelectableIds.length > 0 &&
    pageSelectableIds.every((sid) =>
      selectedIds.some((x) => Number(x) === sid)
    );

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !pageSelectableIds.includes(Number(id)))
      );
    } else {
      setSelectedIds((prev) => [
        ...new Set([...prev.map(Number), ...pageSelectableIds]),
      ]);
    }
  };

  const perPage =
    (props?.per_page && parseInt(props.per_page, 10)) ||
    controller.per_page ||
    25;

  const responsibleLabel = (item) =>
    item?.responsible_participant?.user?.displayTitle ||
    item?.responsible_participant?.user?.name ||
    "—";

  const executorLabel = (item) =>
    item?.responsible_executor_participant?.user?.displayTitle ||
    item?.responsible_executor_participant?.user?.name ||
    "—";

  return (
    <>
      <div className="min-w-0 space-y-6 rounded-lg bg-white px-4 py-6 shadow-sm sm:px-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
        <ModuleFilter
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={filters}
          acceptedStatuses={WORK_ORDER_STATUS_INCLUSION}
          searchPlaceholder={t("Search work orders...")}
          filterTitle={t("Filter Work Orders by Status")}
          customCheckboxButtonFilters={customCheckboxButtonFilters}
          userOptions={executorUserOptions}
          usersFilterLabel={t("work_orders.filter_executor")}
        />
      </div>

      {canUseBulkOrderTool && selectedIds.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
          <span className="text-sm font-medium text-amber-950">
            {t("work_orders.bulk_create.toolbar", {
              count: selectedIds.length,
            })}
          </span>
          <button
            type="button"
            className="rounded-lg border border-amber-800/30 bg-amber-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-950"
            onClick={() => setBulkDialogOpen(true)}
          >
            {t("work_orders.bulk_create.open")}
          </button>
          <button
            type="button"
            className="rounded-lg border border-amber-300/80 bg-white px-3 py-1.5 text-sm font-medium text-amber-950 hover:bg-amber-100/80"
            onClick={() => setSelectedIds([])}
          >
            {t("work_orders.bulk_create.clear")}
          </button>
        </div>
      ) : null}

      <div className="min-w-0 space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
        <div className="flow-root min-w-0">
          <PMPagination
            disabled={isLoading}
            onPageChange={handlePageChange}
            page={controller.page}
            perPage={perPage}
            totalCount={totalCount}
            currentCount={currentCount}
            showCount={true}
            showPages={true}
            showNextPrev={true}
          />

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            {isLoading ? (
              <div className="p-8">
                <ResourceLoading />
              </div>
            ) : error ? (
              <div className="p-6">
                <PMNotice title="Kunne ikke laste inn stats" />
              </div>
            ) : data.length === 0 ? (
              <div className="p-6">
                <ResourceEmpty />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[56rem] table-fixed border-collapse divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {canUseBulkOrderTool ? (
                        <th className="w-10 px-2 py-2.5 text-left xl:w-12 xl:px-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-400 text-teal-700 focus:ring-teal-500"
                            checked={allPageSelected}
                            disabled={pageSelectableIds.length === 0}
                            onChange={toggleSelectAllPage}
                            aria-label={t("work_orders.bulk_create.select_page")}
                          />
                        </th>
                      ) : null}
                      <th className="w-[5rem] px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:w-24 xl:px-3">
                        {t("Work Order #")}
                      </th>
                      <th className="min-w-[14rem] px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:min-w-[18%] xl:px-3">
                        {t("Title")}
                      </th>
                      <th className="w-[9%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("status")}
                      </th>
                      <th className="w-[12%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("customer")}
                      </th>
                      <th className="w-[12%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("Project")}
                      </th>
                      <th className="w-[10%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("Responsible")}
                      </th>
                      <th className="w-[10%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("Executor")}
                      </th>
                      <th className="w-[7rem] shrink-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("Order Date")}
                      </th>
                      <th className="w-[7rem] shrink-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                        {t("Due Date")}
                      </th>
                      <th className="relative w-9 px-1 py-2.5 xl:w-10 xl:px-2">
                        <span className="sr-only">{t("Open")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {data.map((item) => (
                      <tr
                        key={item.id}
                        onClick={(e) => {
                          if (e.target?.closest?.("[data-bulk-check-wrap]"))
                            return;
                          navigate(`/dashboard/work_orders/${item.id}`);
                        }}
                        className="group cursor-pointer transition-colors hover:bg-gray-50"
                      >
                        {canUseBulkOrderTool ? (
                          <td
                            className="px-2 py-2 align-middle xl:px-3"
                            data-bulk-check-wrap
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-400 text-teal-700 focus:ring-teal-500 disabled:opacity-40"
                              disabled={!canSelectWorkOrderForBulk(item)}
                              checked={selectedIds.some(
                                (x) => Number(x) === normalizeWoId(item.id)
                              )}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleRowSelection(
                                  item.id,
                                  canSelectWorkOrderForBulk(item)
                                );
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t("work_orders.bulk_create.select_row")}
                            />
                          </td>
                        ) : null}
                        <td className="truncate px-2 py-2 text-sm font-medium text-blue-600 xl:px-3">
                          {item?.number ?? "—"}
                        </td>
                        <td className="min-w-0 px-2 py-2 xl:px-3">
                          <div
                            className="truncate text-sm font-medium text-gray-900"
                            title={
                              item?.title || item?.displayTitle || undefined
                            }
                          >
                            <StringLimit
                              text={
                                item?.title ||
                                item?.displayTitle ||
                                t("work_orders.table_no_title")
                              }
                              limit={56}
                            />
                          </div>
                        </td>
                        <td className="min-w-0 px-2 py-2 xl:px-3">
                          <ItemStatus item={item} />
                        </td>
                        <td className="min-w-0 px-2 py-2 xl:px-3">
                          <p
                            className="truncate text-sm text-gray-600"
                            title={
                              item?.customer?.displayTitle ||
                              item?.customer?.name ||
                              undefined
                            }
                          >
                            {item?.customer?.displayTitle ||
                              item?.customer?.name ||
                              "—"}
                          </p>
                        </td>
                        <td className="min-w-0 px-2 py-2 xl:px-3">
                          <p
                            className="truncate text-sm text-gray-600"
                            title={
                              item?.project?.displayTitle ||
                              item?.project?.name ||
                              undefined
                            }
                          >
                            {item?.project?.displayTitle ||
                              item?.project?.name ||
                              "—"}
                          </p>
                        </td>
                        <td className="min-w-0 px-2 py-2 xl:px-3">
                          <p
                            className="truncate text-sm text-gray-600"
                            title={responsibleLabel(item)}
                          >
                            {responsibleLabel(item)}
                          </p>
                        </td>
                        <td className="min-w-0 px-2 py-2 xl:px-3">
                          <p
                            className="truncate text-sm text-gray-600"
                            title={executorLabel(item)}
                          >
                            {executorLabel(item)}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-600 xl:px-3">
                          {item?.order_date
                            ? dateToString(item.order_date)
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-600 xl:px-3">
                          {item?.due_date ? dateToString(item.due_date) : "—"}
                        </td>
                        <td className="px-1 py-2 text-right align-middle xl:px-2">
                          <ChevronRightIcon className="inline h-5 w-5 text-gray-400 transition group-hover:text-gray-600" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <WorkOrderBulkCreateOrderDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        workOrderIds={selectedIds}
        onDone={() => {
          setSelectedIds([]);
          getWorkOrders();
        }}
      />
    </>
  );
};
